import React, { useState, useEffect, useRef } from "react";
import { Container, Table, Button, Form, Card } from "react-bootstrap";
import axios from "axios";
import moment from "moment";
import "moment-timezone";
import "bootstrap/dist/css/bootstrap.min.css";

const Mody = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedRows, setSavedRows] = useState({});
  const pendingSaves = useRef({});
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("theme") === "dark");
  const [savingServiceKey, setSavingServiceKey] = useState(null);
  // Saving is manual, so the page has to remember what came back from the server
  // to know which rows still differ. Keyed by `service` (the real primary key —
  // two rows can share a panel id).
  const [pristine, setPristine] = useState({});
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get("https://api.viralgpt.com/boosts/services");
        setServices(response.data);
        setPristine(Object.fromEntries(response.data.map((s) => [s.service, JSON.stringify(rowValues(s))])));
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch services:", error);
        alert("Failed to fetch services: " + (error.response?.data?.error || error.message));
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  // Only the fields this table can actually edit. Comparing whole rows would
  // flag every row dirty, because the server returns columns the form never
  // touches (lastChecked, status, dropRate…).
  const EDITABLE = ["id", "price", "minimumQuantity", "notes", "averageTime", "website", "enabled", "fallback_service_id"];
  const rowValues = (s) => EDITABLE.map((k) => (s[k] === null || s[k] === undefined ? "" : String(s[k])));
  const isDirty = (s) => pristine[s.service] !== undefined && pristine[s.service] !== JSON.stringify(rowValues(s));
  const dirtyCount = services.filter(isDirty).length;

  // Auto-save is off, so a stray navigation is now the way edits get lost.
  useEffect(() => {
    if (!dirtyCount) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirtyCount]);

  useEffect(() => {
    document.body.classList.toggle("dark-mode", darkMode);
    localStorage.setItem("theme", darkMode ? "dark" : "light");
  }, [darkMode]);
  const handleSave = async (service) => {
    if (pendingSaves.current[service.service]) return;

    try {
      pendingSaves.current[service.service] = true;
      setSavingServiceKey(service.service);

      if (service.lastChecked) {
        service.lastChecked = moment(service.lastChecked)
          .tz("Africa/Cairo")
          .format("YYYY-MM-DD");
      }

	  service['status'] = service.status.toLowerCase();
	  service['enabled'] = service.enabled;

      await axios.post("https://api.viralgpt.com/boosts/services", service);

      setServices((prev) =>
        prev.map((s) => (s.service === service.service ? { ...s, ...service } : s))
      );
      setPristine((prev) => ({ ...prev, [service.service]: JSON.stringify(rowValues(service)) }));

      setSavedRows((prev) => ({ ...prev, [service.service]: true }));
      setTimeout(() => {
        setSavedRows((prev) => ({ ...prev, [service.service]: false }));
        setSavingServiceKey(null);
      }, 1000);
    } catch (error) {
      console.error("Failed to save service:", error);
      alert("Failed to update service: " + (error.response?.data?.error || error.message));
      setSavingServiceKey(null);
    } finally {
      delete pendingSaves.current[service.service];
    }
  };

  if (loading) return <div className="text-center mt-5 fw-bold text-muted">Loading, please wait...</div>;

  return (
    <Container fluid className={`py-4 ${darkMode ? "bg-dark text-light" : "bg-white text-dark" } ` }>
      <div className="d-flex justify-content-between align-items-center mb-4 p-3 border-bottom shadow-sm ">
        <h1 className="fw-bold text-primary">
          📋 Service Management
          {dirtyCount > 0 && (
            <span className="ms-3 badge bg-warning text-dark align-middle" style={{ fontSize: "0.5em" }}>
              {dirtyCount} unsaved {dirtyCount === 1 ? "row" : "rows"} — press Save on each
            </span>
          )}
        </h1>
        <Button onClick={() => setDarkMode(!darkMode)} variant="outline-primary" className="shadow-sm rounded-pill">
          {darkMode ? "☀️ Light Mode" : "🌙 Dark Mode"}
        </Button>
      </div>
	  
      <div className="table-responsive" style={{ maxHeight: "90vh", overflowY: "auto" }}>
        <Table striped bordered hover responsive variant={darkMode ? "dark" : "light"} className="shadow-sm rounded overflow-hidden">
          <thead className="table-primary text-uppercase text-center" style={{ position: "sticky", top: 0, zIndex: 1 }}>
            <tr>
              <th>Status</th>
              <th>Name</th>
              <th>Service ID</th>
              <th>Price</th>
              <th>Min Qty</th>
              <th>Notes</th>
              <th>Avg Time</th>
              <th>Website</th>
              <th>Fallback</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody className="text-start">
            {services.map((service) => (
              <tr key={service.service} style={{ backgroundColor: savedRows[service.service] ? "#90EE90" : isDirty(service) ? "#fff7ed" : undefined }}>
                <td>
                  <Form.Check
                    type="switch"
                    checked={service.enabled !== 0}
                    onChange={(e) => {
                      const updatedService = { ...service, enabled: e.target.checked ? 1 : 0 };
                      setServices((prev) => prev.map((s) => (s.service === service.service ? updatedService : s)));
                    }}
                    className="d-inline-block me-2"
                  />
                  {service.status === 'working' && '✅ Working'}
                  {service.status === 'pending' && '♻️ Pending'}
                  {service.status === 'dropping' && '💧 Dropping'}
                  {service.status === 'broken' && '❌ Broken'}
				 
                </td>
                <td >{service.name}</td>
                <td className="text-center">
                  <Form.Control
                    type="number"
                    value={service.id}
                    onChange={(e) => setServices((prev) => prev.map((s) => (s.service === service.service ? { ...s, id: parseInt(e.target.value) } : s)))}
                    className="form-control-sm mx-auto"
                    style={{ width: "100px" }}
                  />
                </td>
                <td className="text-center">
                  <Form.Control
                    type="number"
                    value={service.price}
                    onChange={(e) => setServices((prev) => prev.map((s) => (s.service === service.service ? { ...s, price: parseFloat(e.target.value) } : s)))}
                    className="form-control-sm mx-auto"
                    style={{ width: "100px" }}
                  />
                </td>
                <td className="text-center">
                  <Form.Control
                    type="number"
                    value={service.minimumQuantity}
                    onChange={(e) => setServices((prev) => prev.map((s) => (s.service === service.service ? { ...s, minimumQuantity: parseInt(e.target.value) } : s)))}
                    className="form-control-sm mx-auto"
                    style={{ width: "100px" }}
                  />
                </td>
                <td className="text-center">
                  <Form.Control
                    as="textarea"
                    rows={1}
                    value={service.notes}
                    onChange={(e) => setServices((prev) => prev.map((s) => (s.service === service.service ? { ...s, notes: e.target.value } : s)))}
                    className="form-control-sm mx-auto"
                  />
                </td>
                <td className="text-center">
                  <Form.Control
                    value={service.averageTime}
                    onChange={(e) => setServices((prev) => prev.map((s) => (s.service === service.service ? { ...s, averageTime: e.target.value } : s)))}
                    className="form-control-sm mx-auto"
                    style={{ width: "100px" }}
                  />
                </td>
                <td className="text-center">
                  <Form.Select
                    value={service.website}
                    onChange={(e) => {
                      const updatedService = { ...service, website: e.target.value };
                      setServices((prev) => prev.map((s) => (s.service === service.service ? updatedService : s)));
                    }}
                    className="form-select-sm mx-auto"
                    style={{ width: "180px" }}
                  >
                    <option value="justanotherpanel.com">justanotherpanel.com</option>
                    <option value="smmturk.org">smmturk.org</option>
                  </Form.Select>
                </td>
                <td className="text-center">
                  {/* Where a cancelled order gets re-placed. A panel service id,
                      same as the Service ID column. The backend rejects ids this
                      table does not carry, and rejects a service pointing at
                      itself. Blank = no fallback. */}
                  <Form.Control
                    type="number"
                    value={service.fallback_service_id ?? ""}
                    onChange={(e) =>
                      setServices((prev) =>
                        prev.map((s) => (s.service === service.service
                          ? { ...s, fallback_service_id: e.target.value === "" ? null : parseInt(e.target.value) }
                          : s))
                      )
                    }
                    className="form-control-sm mx-auto"
                    style={{ width: "100px" }}
                  />
                </td>
                <td className="text-center">
                  <Button 
                    size="sm" 
                    variant={isDirty(service) ? "success" : "outline-secondary"} 
                    onClick={() => handleSave(service)}
                    disabled={loading || savingServiceKey === service.service || !isDirty(service)}
                  >
                    {loading ? 'Loading...' : savingServiceKey === service.service ? 'Saved!' : isDirty(service) ? 'Save *' : 'Saved'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

    </Container>
  );
};

export default Mody;
